import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { erpSettlementsApi, type ErpSettlementRow } from "../../api/erpSettlements";
import { FinanceFilters, type FiltersValue } from "../finance/FinanceFilters";
import { presetRange } from "../finance/usePeriod";
import { Registar, type Kolona } from "../../components/Registar";
import { formatDate, formatMoneyCode, formatNumber } from "../../lib/format";

/**
 * The register of settled intakes.
 *
 * The rows are read from the invoices that settled them, because that is where a settlement is made:
 * Bankom raises no settlement document of its own, and the incoming invoice names the receipts it
 * pays for. The portal reads that and does not recompute it.
 *
 * One row is one settlement — an intake and the invoice that paid for it — and not one intake. An
 * intake paid by two invoices has two settlements, and collapsing them would hide the second payment.
 */
export default function SupplierSettlementsPage() {
  const { t } = useTranslation();
  const [valute, setValute] = useState<string[]>([]);
  const [neprepoznatih, setNeprepoznatih] = useState(0);

  // Opens on every settlement, not on the current year: a register is looked things up in, and a
  // period applied by default would make a search for a 2022 receipt come back empty without saying
  // why. Narrowing by period is then the reader's choice, and the search works inside it.
  const init = useMemo<FiltersValue>(() => ({ preset: "all", ...presetRange("all") }), []);
  const [filters, setFilters] = useState<FiltersValue>(init);

  // The currencies and the unresolved count do not follow the filter: the picker must offer what the
  // register holds, and the count is about what the register leaves out either way.
  useEffect(() => {
    void erpSettlementsApi.currencies().then(setValute).catch(() => setValute([]));
    void erpSettlementsApi
      .unresolved()
      .then((r) => setNeprepoznatih(r.settledWithoutInvoice))
      .catch(() => setNeprepoznatih(0));
  }, []);

  const onChange = (v: FiltersValue) => {
    const range = v.preset === "custom" ? { from: v.from, to: v.to } : presetRange(v.preset);
    setFilters({ ...v, ...range });
  };

  const kolone: Kolona<ErpSettlementRow>[] = [
    {
      kljuc: "prijemnica",
      zaglavlje: t("settlements.receipt", "Receipt"),
      labela: t("settlements.receipt", "Receipt"),
      crtaj: (r) => r.deliveryNumber ?? "—",
    },
    {
      kljuc: "datum",
      zaglavlje: t("settlements.date", "Received"),
      labela: t("settlements.date", "Received"),
      crtaj: (r) => formatDate(r.settledOn),
    },
    {
      kljuc: "kooperant",
      zaglavlje: t("settlements.supplier", "Supplier"),
      labela: t("settlements.supplier", "Supplier"),
      crtaj: (r) => r.companyName ?? "—",
    },
    {
      kljuc: "neto",
      zaglavlje: t("settlements.netKg", "Net (kg)"),
      labela: t("settlements.netKg", "Net (kg)"),
      broj: true,
      crtaj: (r) => formatNumber(r.netKg),
    },
    {
      kljuc: "racun",
      zaglavlje: t("settlements.invoice", "Invoice"),
      labela: t("settlements.invoice", "Invoice"),
      crtaj: (r) => (
        <>
          {r.invoiceNumber ?? "—"}
          {/* One invoice pays for a loading run, not one truck — up to seventy-nine receipts. Without
              this the amount beside a single intake reads as that intake's. */}
          {r.coveredReceipts > 1 && (
            <span
              className="tag"
              style={{ marginLeft: 6 }}
              title={t("settlements.coversTitle", "This invoice pays for several receipts")}
            >
              {r.resolvedReceipts === r.coveredReceipts
                ? t("settlements.covers", "{{n}} receipts", { n: r.coveredReceipts })
                : t("settlements.coversPartly", "{{m}} of {{n}} receipts", {
                    m: r.resolvedReceipts,
                    n: r.coveredReceipts,
                  })}
            </span>
          )}
        </>
      ),
    },
    {
      kljuc: "cena",
      zaglavlje: t("settlements.unitPrice", "Price"),
      labela: t("settlements.unitPrice", "Price"),
      broj: true,
      crtaj: (r) => formatMoneyCode(r.unitPrice, r.currency, undefined, { maximumFractionDigits: 4 }),
    },
    {
      kljuc: "iznos",
      zaglavlje: t("settlements.amount", "For this intake"),
      labela: t("settlements.amount", "For this intake"),
      broj: true,
      crtaj: (r) =>
        r.amountForThisDelivery == null ? (
          <span
            style={{ color: "var(--ink-3)" }}
            title={t(
              "settlements.manyArticlesTitle",
              "The invoice carries several articles, so no amount is attributed to this intake.",
            )}
          >
            {t("settlements.notAttributed", "not attributed")}
          </span>
        ) : (
          <strong>{formatMoneyCode(r.amountForThisDelivery, r.currency)}</strong>
        ),
    },
  ];

  return (
    <Registar<ErpSettlementRow>
      naslov={t("settlements.title", "Settlement register")}
      podnaslov={t(
        "settlements.subtitle",
        "Each intake and the incoming invoice that paid for it, as recorded in the ERP.",
      )}
      filteri={
        <FinanceFilters
          value={filters}
          markets={[]}
          countries={[]}
          currencies={valute}
          searchPlaceholder={t("settlements.search", "Receipt, invoice or supplier")}
          allowAll
          onChange={onChange}
        />
      }
      kljucFiltera={JSON.stringify([filters.from, filters.to, filters.q, filters.currency])}
      filtrirano={Boolean(filters.q || filters.currency || filters.preset !== "all")}
      ucitaj={(strana, velicina) =>
        erpSettlementsApi.list(
          { from: filters.from, to: filters.to, q: filters.q, currency: filters.currency },
          strana,
          velicina,
        )
      }
      kolone={kolone}
      kljucReda={(r) => `${r.deliveryId}-${r.invoiceId}`}
      vezaNaRed={(r) => `/procurement/deliveries/${r.deliveryId}`}
      izvoz={{ putanja: `/exports/erp-settlements.csv${izvozUpit(filters)}`, ime: "obracuni-po-prijemu.csv" }}
      napomena={
        neprepoznatih > 0
          ? t("settlements.unresolved", "{{n}} settled intakes whose invoice is not identified", {
              n: formatNumber(neprepoznatih),
            })
          : undefined
      }
      praznoUzFilter={t("settlements.emptyFiltered", "No settlement matches this filter.")}
    />
  );
}

/** The export carries the SAME filters as the table, built from the same object the list is read with. */
function izvozUpit(f: FiltersValue): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.currency) p.set("currency", f.currency);
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  const s = p.toString();
  return s ? `?${s}` : "";
}
